/**
 * Muse EEG relayed by the Myndchime (Faria) macOS app, which talks to the
 * headset through InterAxon's official SDK and serves every sample on a local
 * WebSocket (LocalWebSocketServer.swift, ws://127.0.0.1:3030):
 *
 *   {"timestamp": <int64>, "eeg1": TP9, "eeg2": AF7, "eeg3": AF8, "eeg4": TP10}
 *
 * one JSON message per sample at 256 Hz. EEG only: no optics or battery. The
 * timestamp's unit is not documented, so it is inferred from its magnitude and
 * used only to notice missing samples; acquisition time comes from counting.
 */
import { CLOCK_BREAK, SensorClock } from "./athena/clock.ts";
import { EEG_RATE, EEG_SITES, SOURCE_LABELS, type MuseSource, type SourceSink } from "./types.ts";

type SocketLike = {
  onopen: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
  close(): void;
};
type SocketFactory = (url: string) => SocketLike;

type Timers = {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
};
const systemTimers: Timers = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

const BATCH_SAMPLES = 16;
const BATCH_MS = 40;
const BACKOFF_MS = [1000, 2000, 5000];
const FIRST_CONNECT_TIMEOUT_MS = 4000;

/** Seconds per unit for an epoch timestamp of this magnitude (s, ms or µs). */
export function timestampUnitSeconds(value: number): number {
  const magnitude = Math.abs(value);
  if (magnitude > 1e14) return 1e-6;
  if (magnitude > 1e11) return 1e-3;
  return 1;
}

export class BridgeSource implements MuseSource {
  readonly deviceKind = "muse" as const;
  readonly kind = "bridge" as const;
  readonly label = SOURCE_LABELS.bridge;
  readonly capabilities = { optics: false, battery: false };
  #url: string;
  #socketFactory: SocketFactory;
  #timers: Timers;
  #socket: SocketLike | null = null;
  #sink: SourceSink | null = null;
  #wanted = false;
  #halted = false;
  #attempt = 0;
  #reconnectTimer: unknown = null;
  #flushTimer: unknown = null;
  #pending: number[][] = EEG_SITES.map(() => []);
  #pendingLost = 0;
  #lastTimestampS: number | null = null;
  #clock = new SensorClock(EEG_RATE);
  #packets = 0;
  #lost = 0;
  /** Samples have flowed before: after a reconnect the timeline restarts as a break. */
  #streamed = false;
  #malformed = 0;

  constructor(url: string, options: { socketFactory?: SocketFactory; timers?: Timers } = {}) {
    this.#url = url;
    this.#socketFactory = options.socketFactory ?? ((target) => new WebSocket(target) as unknown as SocketLike);
    this.#timers = options.timers ?? systemTimers;
  }

  get url(): string { return this.#url; }

  connect(sink: SourceSink): Promise<void> {
    this.#sink = sink;
    this.#wanted = true;
    this.#halted = false;
    sink.status({ phase: "connecting", detail: `opening ${this.#url}`, deviceName: null });
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const timeout = this.#timers.setTimeout(() => {
        if (settled) return;
        settled = true;
        this.#teardownSocket();
        this.#wanted = false;
        reject(new Error(`The local app bridge did not answer at ${this.#url}. Start the bridge app and its socket server, then connect again.`));
      }, FIRST_CONNECT_TIMEOUT_MS);
      this.#open((opened) => {
        if (settled) return;
        settled = true;
        this.#timers.clearTimeout(timeout);
        if (opened) resolve();
        else {
          this.#wanted = false;
          reject(new Error(`Could not reach the local app bridge at ${this.#url}. Start the bridge app and its socket server, then connect again.`));
        }
      });
    });
  }

  #open(onFirstResult?: (opened: boolean) => void): void {
    let socket: SocketLike;
    try {
      socket = this.#socketFactory(this.#url);
    } catch {
      onFirstResult?.(false);
      return;
    }
    this.#socket = socket;
    let opened = false;
    socket.onopen = () => {
      opened = true;
      this.#attempt = 0;
      this.#clock.reset();
      this.#lastTimestampS = null;
      if (this.#streamed) this.#pendingLost = CLOCK_BREAK;
      onFirstResult?.(true);
      onFirstResult = undefined;
      this.#sink?.status({ phase: "streaming", detail: `relaying from ${this.#url}`, deviceName: "Device (via local app bridge)" });
    };
    socket.onmessage = (event) => this.#onMessage(event.data);
    socket.onerror = () => {};
    socket.onclose = () => {
      if (this.#socket !== socket) return;
      this.#socket = null;
      this.#flush();
      if (!opened && onFirstResult) {
        onFirstResult(false);
        return;
      }
      if (!this.#wanted) return;
      const delay = BACKOFF_MS[Math.min(this.#attempt, BACKOFF_MS.length - 1)]!;
      this.#attempt++;
      this.#sink?.status({ phase: "reconnecting", detail: `bridge closed · retrying in ${delay / 1000} s`, deviceName: null });
      this.#reconnectTimer = this.#timers.setTimeout(() => {
        this.#reconnectTimer = null;
        if (this.#wanted) this.#open();
      }, delay);
    };
  }

  #onMessage(data: unknown): void {
    if (this.#halted || typeof data !== "string") return;
    let message: Record<string, unknown>;
    try {
      message = JSON.parse(data);
    } catch {
      this.#malformed++;
      return;
    }
    if (!message || typeof message !== "object" || !("eeg1" in message)) return; // status / metrics frames
    const values = [message.eeg1, message.eeg2, message.eeg3, message.eeg4].map((value) => typeof value === "number" && Number.isFinite(value) ? value : NaN);
    const timestamp = typeof message.timestamp === "number" && Number.isFinite(message.timestamp) ? message.timestamp : null;
    this.#packets++;
    if (timestamp !== null) {
      const seconds = timestamp * timestampUnitSeconds(timestamp);
      if (this.#lastTimestampS !== null) {
        const steps = Math.round((seconds - this.#lastTimestampS) * EEG_RATE);
        if (steps <= 0) {
          // Repeated or reordered sample: drop it rather than invent a timeline.
          if (seconds <= this.#lastTimestampS) return;
        } else if (steps > 1) {
          this.#flush();
          this.#pendingLost += steps > EEG_RATE ? CLOCK_BREAK : steps - 1;
          this.#lost += Math.min(steps - 1, EEG_RATE);
        }
      }
      this.#lastTimestampS = seconds;
    }
    for (let c = 0; c < values.length; c++) this.#pending[c]!.push(values[c]!);
    if (this.#pending[0]!.length >= BATCH_SAMPLES) this.#flush();
    else if (this.#flushTimer === null) this.#flushTimer = this.#timers.setTimeout(() => this.#flush(), BATCH_MS);
  }

  #flush(): void {
    if (this.#flushTimer !== null) this.#timers.clearTimeout(this.#flushTimer);
    this.#flushTimer = null;
    const count = this.#pending[0]!.length;
    if (!count || !this.#sink) {
      this.#pending = EEG_SITES.map(() => []);
      return;
    }
    const now = this.#timers.now();
    const stamp = this.#clock.stamp(count, now, Number.isFinite(this.#pendingLost) ? this.#pendingLost : 0);
    const gapBefore = Number.isFinite(this.#pendingLost) ? stamp.gapBefore : CLOCK_BREAK;
    this.#sink.eeg({ t0: stamp.t0, rate: EEG_RATE, samples: this.#pending.map((values) => Float32Array.from(values)), gapBefore });
    this.#streamed = true;
    this.#sink.diagnostics({ packets: this.#packets, lostPackets: this.#lost, parseErrors: this.#malformed, lastPacketAt: now });
    this.#pending = EEG_SITES.map(() => []);
    this.#pendingLost = 0;
  }

  async halt(): Promise<void> {
    this.#halted = true;
    this.#flush();
    this.#sink?.status({ phase: "halted", detail: "paused", deviceName: "Device (via local app bridge)" });
  }

  async resume(): Promise<void> {
    this.#halted = false;
    this.#clock.reset();
    this.#lastTimestampS = null;
    if (this.#streamed) this.#pendingLost = CLOCK_BREAK;
    this.#sink?.status({ phase: "streaming", detail: `relaying from ${this.#url}`, deviceName: "Device (via local app bridge)" });
  }

  #teardownSocket(): void {
    const socket = this.#socket;
    this.#socket = null;
    if (socket) {
      socket.onclose = socket.onmessage = socket.onopen = null;
      try { socket.close(); } catch {}
    }
  }

  async disconnect(): Promise<void> {
    this.#wanted = false;
    if (this.#reconnectTimer !== null) this.#timers.clearTimeout(this.#reconnectTimer);
    this.#reconnectTimer = null;
    this.#flush();
    this.#teardownSocket();
    this.#sink?.status({ phase: "closed", detail: "", deviceName: null });
    this.#sink = null;
  }
}
